import React, { useEffect, useState } from 'react';
import { useListContext } from 'ra-core';
import {
  FunctionField,
  ListProps,
  TextField,
  ReferenceField,
  NumberField,
  Identifier,
  Link,
  useRedirect,
  RowClickFunction,
  useList,
} from 'react-admin';

import {
  ReferenceInput,
  SelectInput,
  Grid,
  Forbidden,
  TextInput,
  SelectArrayInput,
  NullableBooleanInput,
  DateRangeSelect,
  BooleanField,
  DateField,
  PhoneField,
  MoneyUsdField,
  MoneyField, PercentField,
} from '@components';
import { checkSuperadmin, useSettingsContext } from '@providers';
import { useExporter, useRbacEdit, useRbacList } from '@hooks';
import {
  AdminOptionCats,
  contractStatusOptions,
  Credit, creditNbfiStatusOptions,
  genderOptions, SyntageProfile,
  userSourceOptions,
} from '@types';
import { ButtonLinkField } from '@components/fields/ButtonLinkField';
import { GridList } from '@components/GridList/GridList';
import {
  creditStatusOptions,
  CreditStatusField,
  creditStatusBeforeRejectionOptions,
} from './CreditStatusField';
import { moneyFormatter, rbac } from '@utils';
import { CreditStatesFilter } from './CreditStatesFilter';
import { CreditCropsFilter } from './CreditCropsFilter';
import { CreditListActions } from './CreditListActions';
import { RiskScoreField } from '@pages/credit/RiskScoreField';
import { ContractStatus } from '@pages/contracts/Components/ContractStatus';
import {
  adminHttp,
  adminSettingsHttp,
  creditFileStatusNames,
  CreditFileStatusTypes,
} from '@network';
import { SyntageStatusField } from '@pages/user/SyntageStatusField';
import { CreditDealersFilter } from '@pages/credit/CreditDealersFilter';
import { SyntageProfileField } from '@pages/user/SyntageProfileField';
import { AutocompleteFilter } from '@components/filters/AutocompleteFilter';
import styles from './CreditList.module.css';
import cls from '@pages/contracts/Contracts.module.css';


const Summary = () => {
  const { data, selectedIds } = useListContext<Credit>();
  if (!data || !selectedIds.length) return null;
  const list: Credit[] =
    selectedIds.length > 0
      ? (data || []).filter((i) => selectedIds.includes(i.id))
      : (data || []);
  const creditsTotal = list.reduce((acc, curr) => acc + curr.bankApproval, 0);
  const creditsTotalBalance = list.reduce((acc, curr) => acc + curr.balance, 0);
  const creditsTotalMember = list.reduce((acc, curr) => acc + curr.membership, 0);
  const creditsTotalRetention = list.reduce((acc, curr) => acc + curr.retention, 0);
  const creditsAvg = +(creditsTotal / list.length).toFixed(2);

  return (
    <>
      <span>Total Credit: {moneyFormatter.format(creditsTotal)}</span>
      <span style={{ paddingRight: 14 }} />
      <span>Avg Credit: {moneyFormatter.format(creditsAvg || 0)}</span>
      <span style={{ paddingRight: 14 }} />
      <span>Balance: {moneyFormatter.format(creditsTotalBalance)}</span>
      <span style={{ paddingRight: 14 }} />
      <span>Membership: {moneyFormatter.format(creditsTotalMember)}</span>
      <span style={{ paddingRight: 14 }} />
      <span>Retention: {moneyFormatter.format(creditsTotalRetention)}</span>
    </>
  );
};

const DealerCreditTracker = ({ record, onDealerCredit }: {
  record: Credit;
  onDealerCredit: (id: number, isDealer: boolean) => void;
}) => {
  useEffect(() => {
    onDealerCredit(record.id, record.createdByDealerId != null);
  }, [record.id, record.createdByDealerId, onDealerCredit]);

  return null;
};

export const CreditList = (props: ListProps) => {
  const { filterValues } = useSettingsContext();
  const isPesoAmount = filterValues['isMixed'] === false;

  const exporter = useExporter({
    filename: 'Verqor Credits',
    props: {
      'id': {
        label: 'ID',
      },
      'code': {
        label: 'Code',
      },
      'status': {
        label: 'Status',
        prop: 'statusEn',
      },
      'fileStatus': {
        label: 'File Status',
        transform: (value: CreditFileStatusTypes) => value ? creditFileStatusNames[value] : '',
      },
      'contractStatus': {
        label: 'Contract Status',
        prop: 'contract.statusName',
      },
      'accreditedName': {
        label: 'Accredited',
      },
      'user.phone': {
        label: 'Phone',
        transform: rbac.canShowUserPhone() ? 'phone' : 'masked',
      },
      'user.email': {
        label: 'Email',
      },
      'user.address': {
        label: 'Address',
        prop: 'user.address',
      },
      'user.sourceEn': {
        label: 'Channel',
        prop: 'user.sourceEn',
      },
      'userIpAddress': {
        label: 'User IP address',
        prop: 'userIpAddress',
      },
      'user.marriage': {
        label: 'Civil status',
      },
      'user.stateId': {
        label: 'State',
        prop: 'user.state.name',
      },
      'user.responsibleAdminId': {
        label: 'Responsible Manager',
        prop: 'user.responsibleAdmin.name',
      },
      'collectionsManager.name': {
        label: 'Collections Manager',
        prop: 'collectionsManager.name',
      },
      'mainBuyerName': {
        label: 'Withholding Company',
      },
      'invoicesTitle': {
        label: 'Invoices',
      },
      'bankId': {
        label: 'Bank',
        prop: 'bank.nameMx',
      },
      'nbfiStatus': {
        label: 'NBFI Status',
      },
      'docsLink': {
        label: 'DocsLink',
      },
      'sfOpportunityLink': {
        label: 'Salesforce',
      },
      'progressAt': {
        label: 'Update by client',
        transform: 'datetime',
      },
      'progressPercent': {
        label: 'File progress',
      },
      'modificationAt': {
        label: 'Last update',
        transform: 'datetime',
      },
      'isUsd': {
        label: 'Currency',
        transform: 'currency',
      },
      'exchangeRate': {
        label: 'Exch. Rate',
        transform: 'money',
      },
      'bankApproval': {
        label: 'Credit',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'usd_bank_approval',
      },
      'balance': {
        label: 'Balance',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'usd_balance',
      },
      'requestAmount': {
        label: 'Request Amount',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'usd_requestAmount',
      },
      'farmerDeposit': {
        label: 'Farmer Deposit',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'usd_farmer_deposit',
      },
      'membership': {
        label: 'Membership',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'usd_membership',
      },
      'creditFee': {
        label: 'Withdrawal Fee',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'usd_credit_fee',
      },
      'insurance': {
        label: 'Insurance',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'usd_insurance',
      },
      'refunds': {
        label: 'Refunds',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'usd_refunds',
      },
      'ordersTotalAmount': {
        label: 'Total Orders',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'usd_ordersTotalAmount',
      },
      'retention': {
        label: 'Retention',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'usd_retention',
      },
      'repayment': {
        label: 'Repayment',
        transform: 'money',
        sourceUsd: isPesoAmount ? undefined : 'usd_repayment',
      },
      'interest': {
        label: 'Interests',
        transform: 'moneyNullable',
        sourceUsd: isPesoAmount ? undefined : 'usd_interest',
      },
      'paymentAmount': {
        label: 'Payment Amount',
        transform: 'moneyNullable',
        sourceUsd: isPesoAmount ? undefined : 'usd_paymentAmount',
      },
      'finalPaymentAmount': {
        label: 'Final Payment',
        transform: 'moneyNullable',
        sourceUsd: isPesoAmount ? undefined : 'usd_finalPaymentAmount',
      },
      'creditCropNames': {
        label: 'Financed Crops',
      },
      'financedHa': {
        label: 'Financed Ha',
      },
      'buyerNames': {
        label: 'Buyers',
      },
      'retailerNames': {
        label: 'Retailers',
      },
      'associateNames': {
        label: 'Associates',
      },
      'insuranceCompany': {
        label: 'Insurance Company',
      },
      'cropProblems': {
        label: 'Comments and Progress',
      },
      'rating': {
        label: 'Rate',
      },
      'signatureAt': {
        label: 'Disbursement date',
        transform: 'date',
      },
      'signedContractAt': {
        label: 'Signature of Contract',
        transform: 'date',
      },
      'paymentAt': {
        label: 'Payment At',
        transform: 'date',
      },
      'createdAt': {
        label: 'Created At',
        transform: 'date',
      },
      'isCompany': {
        label: 'Is Company',
        transform: 'boolean',
      },
      'isFixed': {
        label: 'Is Fixed',
        transform: 'boolean',
      },
      'parDelayFixed': {
        label: 'PAR default',
        transform: 'parDelayFixed',
      },
      'parDelayDays': {
        label: 'Delay Days',
      },
      'rejectReason': {
        label: 'Reason for rejection',
      },
      'riskRejectReason': {
        label: 'Risk rejection reason',
      },
      'risk.moratoryRate': {
        label: 'Moratory Rate',
        transform: 'percent',
      },
      'risk.approvedAmount': {
        label: 'Approved Amount',
        transform: 'money',
      },
      'statusBeforeRejection': {
        label: 'Status before rejection',
        transform: 'status',
      },
      'statusChangedAt': {
        label: 'Last Status Change',
        transform: 'datetime',
      },
      'riskScore': {
        label: 'Score',
      },
      'user.activation.activation': {
        label: 'Activation',
      },
      'timerCreatedToAnalysisText': {
        label: 'Applying to Analysis',
      },
      'timerAnalysisToApprovedOrDeniedText': {
        label: 'Analysis to Approved or Denied',
      },
      'timeInApplying': {
        label: 'Time In Applying',
        transform: 'minutes',
      },
      'timeInPreScreening': {
        label: 'Time In Pre-Screening',
        transform: 'minutes',
      },
      'timeInDocCollection': {
        label: 'Time In Doc Collection',
        transform: 'minutes',
      },
      'timeInKycScreening': {
        label: 'Time In KYC Screening',
        transform: 'minutes',
      },
      'timeInPostScreening': {
        label: 'Time In Post Screening',
        transform: 'minutes',
      },
      'timeOfFarmerFile': {
        label: 'Time Of Farmer File',
        transform: 'minutes',
      },
      'timeInCompletedFile': {
        label: 'Time In Completed File',
        transform: 'minutes',
      },
      'timeInAnalysis': {
        label: 'Time In Analysis',
        transform: 'minutes',
      },
      'timeInApproved': {
        label: 'Time In Approved',
        transform: 'minutes',
      },
      'timeInInNegotiations': {
        label: 'Time In In Negotiations',
        transform: 'minutes',
      },
      'timeInContractInProcess': {
        label: 'Time In Contract in process',
        transform: 'minutes',
      },
      'timeInDelay': {
        label: 'Time In Delay',
        transform: 'minutes',
      },
      'timeApprovalToContractGen': {
        label: 'Approval to Contract Gen',
      },
      'timeInContractCreation': {
        label: 'Contract Creation Time',
        transform: 'minutes',
      },
      'timeInContractSending': {
        label: 'Contract Sending Time',
        transform: 'minutes',
      },
      'timeApplyingToSignature': {
        label: 'Applying to Signature',
      },
      'ptCapitalBalance': {
        label: '[P-T] Capital Balance',
        transform: 'moneyNullable',
        sourceUsd: isPesoAmount ? undefined : 'usd_ptCapitalBalance',
      },
      'ptFinalBalance': {
        label: '[P-T] Final Balance',
        transform: 'moneyNullable',
        sourceUsd: isPesoAmount ? undefined : 'usd_ptFinalBalance',
      },
      'pfFinalBalance': {
        label: '[P-F] Final Balance',
        transform: 'moneyNullable',
        sourceUsd: isPesoAmount ? undefined : 'usd_pfFinalBalance',
      },
      'user.syntageProfile.credentialStatus': {
        label: 'CIEC Status',
        transform: 'uppercase',
      },
      'user.syntageProfile': {
        label: 'SAT Profile',
        transform: (syntageProfile: SyntageProfile) => syntageProfile ? 'Created' : 'Missing',
      },
      'user.syntageProfile.createdAt': {
        label: 'SAT Timestamp',
        transform: 'datetime',
      },
    },
  });

  const isSuperadmin = checkSuperadmin();
  const rbacList = useRbacList();
  const rbacEdit = useRbacEdit();
  const redirect = useRedirect();

  const { selectedIds } = useList(props);
  const [rejectReasonOptions, setRejectReasonOptions] = useState<{id?: number, name: string}[]>([]);
  const [adminOptions, setAdminOptions] = useState<{id?: number, name: string}[]>([]);
  const [respManagerOptions, setRespManagerOptions] = useState<{id?: number, name: string}[]>([]);
  const [dealerCreditIds, setDealerCreditIds] = useState<Set<Identifier>>(
    () => new Set(),
  );

  const handleDealerCredit = React.useCallback((id: number, isDealer: boolean) => {
    setDealerCreditIds(prev => {
      const next = new Set(prev);

      if (isDealer) {
        next.add(id);
      } else {
        next.delete(id);
      }

      return next;
    });
  }, []);

  const hasSelectedDealerCredit = selectedIds.some(id =>
    dealerCreditIds.has(id),
  );

  const bulkEditLockText = hasSelectedDealerCredit
    ? 'QC-Credits cannot be edited'
    : undefined;

  useEffect(() => {
    (async () => {
      try {
        const fetchReasons = adminSettingsHttp
          .getOptionsByCategory(AdminOptionCats.SALES_CREDITS_REASON_FOR_REJECTION);
        const fetchOwners = adminHttp.getAll();
        const fetchResManagers = adminHttp.getResponsibleManagers();

        const [reasons, owners, resManagers] =
          await Promise.all([fetchReasons, fetchOwners, fetchResManagers]);

        if (reasons.length) {
          setRejectReasonOptions(reasons.map(o => ({ id: o.id, name: o.name })));
        }
        if (owners.length) {
          setAdminOptions(owners
            .map(o => ({ id: o.id, name: o.name }))
            .sort((a, b) => a.name.localeCompare(b.name)),
          );
        }
        if (resManagers.length) {
          setRespManagerOptions(resManagers
            .map(o => ({ id: o.id, name: o.name }))
            .sort((a, b) => a.name.localeCompare(b.name)),
          );
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  if (!rbacList) {
    return <Forbidden />;
  }

  const creditRowClick: RowClickFunction = (id: Identifier, resource: string, record: any) => {
    redirect(`/${resource}/${id}?initial`);

    return false;
  };

  return (
    <GridList {...props}
      editComponent={rbacEdit ? <CreditListActions /> : undefined}
      exporter={exporter}
      filters={[...Filters,
        <SelectArrayInput
          label="Reason for rejection"
          key="rejectReasonIds"
          source="rejectReasonIds"
          choices={rejectReasonOptions}
          className="MS-field"
          style={{ width: 250 }}
        />,
        <SelectArrayInput
          label="Owners"
          key="contactOwnerIds"
          source="contactOwnerIds"
          choices={adminOptions}
          className="MS-field"
          style={{ width: 350 }}
        />,
        <SelectArrayInput
          label="Responsible Managers"
          key="responsibleAdminIds"
          source="responsibleAdminIds"
          choices={respManagerOptions}
          className="MS-field"
          style={{ width: 350 }}
        />,
      ]}
      sort={{ field: 'id', order: 'DESC' }}
      hideRemoveButton={!rbac.isSuperadmin()}
      disableExportButton={!rbacEdit && selectedIds.length === 0}
      confirmRemoval
      summary={<Summary />}
      bulkEditLockText={bulkEditLockText}
    >
      <Grid
        rowClick={creditRowClick}
        rowStyle={(record: any) => record.unvisited ? { border: 'transparent' } : {}}
        sx={{
          '& .RaDatagrid-row[style="border: transparent;"] .MuiTableCell-paddingCheckbox': {
            backgroundColor: '#647191',
          },
        }}
        defaultColumns={'code status accreditedName user.phone user.hubspotLink bankApproval balance requestAmount riskScore'.split(' ')}
      >
        <FunctionField source="code" sortBy="code" label="Code" render={(record: any) => (
          <>
            <DealerCreditTracker record={record} onDealerCredit={handleDealerCredit} />
            <Link to={`/credit/${record.id}?initial`}>
              <TextField source="code" label="Credit code" className={cls.codeText} />
            </Link>
          </>
        )} />
        <CreditStatusField source="status" label="Status" />
        <FunctionField
          source="fileStatus"
          label="File Status"
          render={(c: Credit) =>
            <span>{c.fileStatus ? creditFileStatusNames[c.fileStatus] : ''}</span>}
        />
        <FunctionField
          source="contractStatus"
          label="Contract Status"
          render={(c: Credit) => (
            <ContractStatus status={c.contractStatus} className={cls.contractStatus} />
          )}
        />
        <FunctionField
          source="accreditedName"
          label="Accredited"
          render={(record: any) => (
            <Link to={`/credit/${record.id}?initial`}>
              <TextField source="accreditedName" label="Accredited" className={cls.codeText} />
            </Link>
          )}
        />
        <PhoneField source="user.phone" label="Phone" isMasked={!rbac.canShowUserPhone()} />
        <TextField source="user.email" label="Email" />
        <TextField source="user.address" label="Address" sortable={false} />
        <TextField source="userIpAddress" label="User IP address" sortable={false} />
        <TextField source="user.marriage" label="Civil status" />
        <TextField source="user.sourceEn" label="Channel" />
        <TextField source="user.contactOwner.name" label="Owner" />
        <FunctionField
          source="mainBuyerName"
          sortBy="mainBuyer.name"
          label="Withholding Company"
          render={(record: any) => record.mainBuyerName || ''}
        />
        {isSuperadmin ? (
          <ReferenceField reference="state" source="user.stateId" label="State">
            <TextField source="name" />
          </ReferenceField>
        ) : (
          <TextField source="user.state.name" label="State" />
        )}
        {isSuperadmin ? (
          <ReferenceField
            reference="admin"
            source="user.responsibleAdminId"
            label="Responsible manager"
          >
            <TextField source="name" />
          </ReferenceField>
        ) : (
          <TextField source="user.responsibleAdmin.name" label="Responsible manager" />
        )}
        <TextField source="collectionsManager.name" label="Collections manager" />
        <TextField source="invoicesTitle" label="Invoices" />
        {isSuperadmin ? (
          <ReferenceField reference="bank" source="bankId" label="Bank">
            <TextField source="nameMx" />
          </ReferenceField>
        ) : (
          <TextField source="bank.nameMx" label="Bank" />
        )}
        <TextField source="nbfiStatus" label="NBFI Status" />
        <ButtonLinkField source="docsLink" text="DocsLink" label="Docs Link" />
        <ButtonLinkField source="sfOpportunityLink"
          text="SalesForce"
          label="SalesForce"
          center
          isBlue />
        <DateField source="progressAt" label="Update by client" showTime />
        <TextField source="progressPercent" label="File progress" />
        <DateField source="modificationAt" label="Last update" showTime />
        <BooleanField source="isUsd" label="Currency" valueLabelTrue="USD" valueLabelFalse="MXN" />
        <MoneyField source="exchangeRate" label="Exch. Rate" />
        <MoneyUsdField source="bankApproval" sourceUsd="usd_bank_approval" label="Credit"
          style={{ display: 'inline-block', width: 100 }} />
        <MoneyUsdField source="balance" sourceUsd="usd_balance"
          style={{ display: 'inline-block', width: 100 }}
          label="Balance" />
        <MoneyUsdField source="requestAmount" sourceUsd="usd_requestAmount"
          style={{ display: 'inline-block', width: 100 }}
          label="Request Amount" />
        <MoneyUsdField source="farmerDeposit" sourceUsd="usd_farmer_deposit"
          label="Farmer Deposit" />
        <MoneyUsdField source="membership" sourceUsd="usd_membership" label="Membership" />
        <MoneyUsdField source="creditFee" sourceUsd="usd_credit_fee" label="Withdrawal fee" />
        <MoneyUsdField source="insurance" sourceUsd="usd_insurance" label="Insurance" />
        <MoneyUsdField source="refunds" sourceUsd="usd_refunds" label="Refunds" />
        <MoneyUsdField source="ordersTotalAmount" sourceUsd="usd_ordersTotalAmount"
          label="Total Orders" />
        <MoneyUsdField source="retention" sourceUsd="usd_retention" label="Retention" />
        <MoneyUsdField source="repayment" sourceUsd="usd_repayment" label="Repayment" />
        <MoneyUsdField source="interest" sourceUsd="usd_interest" label="Interests" />
        <MoneyUsdField source="paymentAmount" sourceUsd="usd_paymentAmount"
          label="Payment Amount" />
        <MoneyUsdField source="finalPaymentAmount" sourceUsd="usd_finalPaymentAmount"
          label="Final Payment" />
        <TextField source="creditCropNames" label="Financed Crops" />
        <NumberField source="financedHa" label="Financed Ha" />
        <TextField source="buyerNames" label="Buyers" sortable={false} />
        <TextField source="retailerNames" label="Retailers" sortable={false} />
        <TextField source="associateNames" label="Associates" sortable={false} />
        <TextField source="insuranceCompany" label="Insurance company" />
        <TextField source="cropProblems" label="Comments and Progress" />
        <TextField source="rating" label="Rate" />
        <DateField source="signatureAt" label="Disbursement date" />
        <DateField source="signedContractAt" label="Signature of Contract" />
        <DateField source="paymentAt" label="Payment At" />
        <DateField source="createdAt" label="Created At" showTime />
        <BooleanField source="isCompany" label="Is Company" />
        <BooleanField source="isFixed" label="Is Fixed" />
        <FunctionField source="parDelayFixed" label="PAR default"
          render={(credit: Credit) => (
            <div>{credit.parDelayEdited
              ? `${credit.parDelayFixed} / manual`
              : credit.parDelayFixed
            }</div>
          )}
        />
        <TextField source="parDelayDays" label="Delay Days" />
        <TextField source="rejectReason" label="Reason for rejection" />
        <TextField source="riskRejectReason" label="Risk rejection reason" />
        <PercentField source="risk.moratoryRate" label="Moratory Rate" />
        <MoneyUsdField source="risk.approvedAmount" nullable label="Approved Amount" />
        <CreditStatusField source="statusBeforeRejection" label="Status before rejection" />
        <DateField source="statusChangedAt" label="Last Status Change" showTime />
        <RiskScoreField source="riskScore" label="Score" />
        <TextField source="timerCreatedToAnalysisText" label="Applying to Analysis" />
        <TextField source="timerAnalysisToApprovedOrDeniedText"
          label="Analysis to Approved or Denied" />
        <TextField source="timeInApplying" label="Time In Applying" />
        <TextField source="timeInPreScreening" label="Time In Pre-Screening" />
        <TextField source="timeInDocCollection" label="Time In Doc Collection" />
        <TextField source="timeInKycScreening" label="Time In KYC Screening" />
        <TextField source="timeInPostScreening" label="Time In Post Screening" />
        <TextField source="timeOfFarmerFile" label="Time Of Farmer File" />
        <TextField source="timeInCompletedFile" label="Time In Completed File" />
        <TextField source="timeInAnalysis" label="Time In Analysis" />
        <TextField source="timeInApproved" label="Time In Approved" />
        <TextField source="timeInInNegotiations" label="Time In In Negotiations" />
        <TextField source="timeInContractInProcess" label="Time In Contract in process" />
        <TextField source="timeInDelay" label="Time In Delay" />
        <TextField source="timeApprovalToContractGen" label="Approval to Contract Gen" />
        <TextField source="timeInContractCreation" label="Contract Creation Time" />
        <TextField source="timeInContractSending" label="Contract Sending Time" />
        <TextField source="timeApplyingToSignature" label="Applying to Signature" />
        <TextField source="timeInSignature" label="Time Spent in Signature" />
        <TextField source="user.activation.activation" label="Activation" />
        <MoneyUsdField source="ptCapitalBalance" sourceUsd="usd_ptCapitalBalance"
          nullable label="[P-T] Capital Balance" />
        <MoneyUsdField source="ptFinalBalance" sourceUsd="usd_ptFinalBalance"
          nullable label="[P-T] Final Balance" />
        <MoneyUsdField source="pfFinalBalance" sourceUsd="usd_pfFinalBalance"
          nullable label="[P-F] Final Balance" />
        <SyntageStatusField source="user.syntageProfile.credentialStatus" label="CIEC Status" />
        <SyntageProfileField source="user.syntageProfile" label="SAT Profile" />
        <DateField source="user.syntageProfile.createdAt" label="SAT Timestamp" showTime />
      </Grid>
    </GridList>
  );
};

const Filters = [
  <TextInput key="q" source="q" label="Search" alwaysOn style={{ width: 200 }} />,
  <SelectArrayInput
    key="contractStatus"
    source="contractStatus"
    label="Contract Status"
    choices={contractStatusOptions}
    className="MS-field"
    style={{ width: 350 }}
  />,
  <TextInput key="accreditedName" source="accreditedName" label="Accredited"
    style={{ width: 220 }} />,
  <ReferenceInput
    key="activationId"
    source="activationId"
    reference="finance-activation"
    label="Activation"
    allowEmpty
    perPage={9999999999}
    sort={{ field: 'name', order: 'ASC' }}
  >
    <SelectInput optionText="activation" label="Activation" style={{ minWidth: 180 }} />
  </ReferenceInput>,
  <ReferenceInput
    key="bankId"
    source="bankId"
    reference="bank"
    label="Bank"
    allowEmpty
    perPage={9999999999}
    sort={{ field: 'nameMx', order: 'ASC' }}
  >
    <SelectInput optionText="nameMx" />
  </ReferenceInput>,
  <SelectArrayInput
    key="sourceEn"
    source="sourceEn"
    label="Channel"
    choices={userSourceOptions}
    className="MS-field"
    style={{ width: 250 }}
  />,
  <TextInput key="creditCodes" source="creditCodes" clearAlwaysVisible />,
  <ReferenceInput
    key="collectionsManagerId"
    source="collectionsManagerId"
    reference="admin/collectors"
    label="Collections Manager"
    allowEmpty
    perPage={9999999999}
    sort={{ field: 'name', order: 'ASC' }}
  >
    <SelectInput optionText="name" label="Collections Manager" style={{ minWidth: 180 }} />
  </ReferenceInput>,
  <CreditCropsFilter key="crops" source="crops" label="Financed crops" />,
  <SelectInput
    key="progress"
    source="progress"
    label="File progress"
    choices={[
      { id: 'completed', name: 'Completed file' },
      { id: 'incomplete', name: 'Incomplete files' },
    ]}
    style={{ width: 200 }}
  />,
  <SelectInput
    key="fileStatus"
    source="fileStatus"
    label="File status"
    choices={[
      {
        id: CreditFileStatusTypes.PRE_SCREENING,
        name: creditFileStatusNames[CreditFileStatusTypes.PRE_SCREENING],
      },
      {
        id: CreditFileStatusTypes.DOC_COLLECTION,
        name: creditFileStatusNames[CreditFileStatusTypes.DOC_COLLECTION],
      },
      {
        id: CreditFileStatusTypes.KYC_SCREENING,
        name: creditFileStatusNames[CreditFileStatusTypes.KYC_SCREENING],
      },
      {
        id: CreditFileStatusTypes.POST_SCREENING,
        name: creditFileStatusNames[CreditFileStatusTypes.POST_SCREENING],
      },
    ]}
    style={{ width: 200 }}
  />,
  <DateRangeSelect
    key="createdAt"
    source="createdAt"
    label="Created/Disbursement/Payment/Last update"
    labelClass={styles.labelClass}
    alwaysOn
    choices={[
      { id: 'createdAt', name: 'Created At' },
      { id: 'signatureAt', name: 'Disbursement date' },
      { id: 'paymentAt', name: 'Payment At' },
      { id: 'modificationAt', name: 'Last update' },
      { id: 'progressAt', name: 'Update by client' },
      { id: 'statusChangedAt', name: 'Last status change' },
      { id: 'signedContractAt', name: 'Signature of Contract' },
    ]}
  />,
  <SelectInput key="gender" source="gender" choices={genderOptions} />,
  <SelectInput
    key="invoices"
    source="invoices"
    label="Invoices"
    choices={[
      { id: 'uploaded', name: 'With invoices' },
      { id: 'empty', name: 'Not uploaded' },
    ]}
    style={{ width: 200 }}
  />,
  <SelectInput
    key="verqorCredit"
    source="verqorCredit"
    label="Verqor Credit"
    defaultValue="yes"
    choices={[
      { id: 'yes', name: 'Yes' },
      { id: 'no', name: 'No' },
      { id: 'mixed', name: 'Mixed' },
    ]}
    style={{ width: 200 }}
  />,
  <AutocompleteFilter
    key="createdByDealerId"
    source="createdByDealerId"
    reference="dealer/by-qc"
    listFieldName="list"
    label="Lender"
    style={{ width: 400 }}
  />,
  <NullableBooleanInput key="isCompany" source="isCompany" label="Is Company" />,
  <NullableBooleanInput key="isFixed" source="isFixed" label="Is Fixed" />,
  <NullableBooleanInput key="renewals" source="renewals" label="Renewals" />,
  <AutocompleteFilter
    key="mainBuyerId"
    source="mainBuyerId"
    reference="credit/options"
    listFieldName="buyerOptions"
    label="Withholding Company"
    style={{ width: 400 }}
  />,
  <CreditDealersFilter key="buyers" source="buyers" label="Buyers" />,
  <CreditDealersFilter key="retailers" source="retailers" label="Retailers" />,
  <CreditDealersFilter key="associates" source="associates" label="Associates" />,
  <SelectInput
    key="delayPar"
    source="delayPar"
    label="PAR default"
    choices={[
      { id: '0', name: '0' },
      { id: 'Default', name: 'Default' },
      { id: '90', name: '90' },
      { id: '60', name: '60' },
      { id: '30', name: '30' },
      { id: 'Recovered', name: 'Recovered' },
    ]}
    style={{ width: 120 }}
  />,
  <SelectArrayInput
    key="status"
    source="status"
    choices={creditStatusOptions}
    className="MS-field"
    style={{ width: 350 }}
    alwaysOn
  />,
  <SelectArrayInput
    key="nbfiStatus"
    source="nbfiStatus"
    label="NBFI Status"
    choices={creditNbfiStatusOptions}
    className="MS-field"
    style={{ width: 350 }}
  />,
  <SelectArrayInput
    key="statusBeforeRejection"
    source="statusBeforeRejection"
    choices={creditStatusBeforeRejectionOptions}
    className="MS-field"
    style={{ width: 350 }}
  />,
  <CreditStatesFilter key="state" source="state" label="State" />,
  <NullableBooleanInput key="isDemo" source="isDemo" label="Show Demo" />,
  <NullableBooleanInput key="unvisited" source="unvisited" label="Unread credits" />,
  <NullableBooleanInput key="isUsd"
    source="isUsd"
    label="Currency"
    trueLabel="USD"
    falseLabel="MXN"
  />,
  <NullableBooleanInput key="isMixed"
    source="isMixed"
    label="Currency Selection"
    nullLabel=""
    defaultValue={true}
    trueLabel="Mixed"
    falseLabel="MXN"
  />,
];
